// The town square: every seat around a circle in table order, this phone's seat at the bottom.
// Used for the lobby, the day, night choices (pick mode) and ballots (vote badges).
//
// The SVG only draws. Each seat that takes a tap is a real <button> laid over its circle: on a
// real iPhone, Safari did not turn taps on SVG seats into clicks, while buttons work everywhere.
import type { CSSProperties, ReactNode } from "react";
import { seatLayout, shortName } from "../lib/seatGeometry";
import type { NominationView, PlayerView, SeatPublic, VoteView } from "../protocol";
import styles from "./TownSquare.module.css";

const SIZE = 320;

export interface TownSquareProps {
  view: PlayerView;
  onSeatTap?: (seatId: string) => void;
  /** Seats that respond to taps (default: every seat, when onSeatTap is given). */
  isTappable?: (seat: SeatPublic) => boolean;
  /** Pick mode: only these seats can be chosen; the rest are dimmed. */
  candidates?: string[] | null;
  selected?: string[];
  nomination?: NominationView | null;
  blockSeatId?: string | null;
  showReady?: boolean;
  children?: ReactNode;
}

export function TownSquare({
  view,
  onSeatTap,
  isTappable,
  candidates,
  selected = [],
  nomination,
  blockSeatId,
  showReady = false,
  children,
}: TownSquareProps) {
  const layout = seatLayout(
    view.seats.map((s) => s.seat_id),
    view.me_seat_id,
    SIZE,
  );
  const bySeat = new Map(view.seats.map((s) => [s.seat_id, s]));
  const votes = new Map((nomination?.votes ?? []).map((v) => [v.seat_id, v]));
  const r = layout.nodeRadius;
  const c = SIZE / 2;
  const pickMode = candidates != null;
  const seats = layout.points.map((p) => {
    const seat = bySeat.get(p.seatId)!;
    const isCandidate = candidates ? candidates.includes(seat.seat_id) : false;
    const tappable = !!onSeatTap && (pickMode ? isCandidate : isTappable ? isTappable(seat) : true);
    return { p, seat, isCandidate, tappable, isSelected: selected.includes(seat.seat_id) };
  });

  return (
    <div className={styles.wrap} role="group" aria-label="Town square">
      <svg className={styles.svg} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true">
        <circle className={styles.table} cx={c} cy={c} r={Math.max(0, layout.ringRadius - r - 6)} />
        {seats.map(({ p, seat, isCandidate, isSelected }) => {
          const vote = votes.get(seat.seat_id);
          const isCurrentVoter = nomination?.current_voter_seat_id === seat.seat_id;
          const classes = [
            !seat.alive && styles.dead,
            seat.seat_id === view.me_seat_id && styles.me,
            pickMode && isCandidate && styles.candidate,
            pickMode && !isCandidate && styles.disabled,
            isSelected && styles.selected,
            nomination?.nominee_seat_id === seat.seat_id && styles.nominee,
            nomination?.nominator_seat_id === seat.seat_id && styles.nominator,
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <g key={seat.seat_id} className={classes || undefined}>
              {isCurrentVoter && <circle className={styles.pulse} cx={p.x} cy={p.y} r={r + 3} />}
              {blockSeatId === seat.seat_id && (
                <circle className={styles.block} cx={p.x} cy={p.y} r={r + 6} />
              )}
              <circle className={styles.node} cx={p.x} cy={p.y} r={r} />
              <text className={styles.initial} x={p.x} y={p.y} fontSize={r * 0.85}>
                {seat.alive ? initials(seat.name) : "✝"}
              </text>
              <text className={styles.name} x={p.x} y={p.y + r + 12}>
                {shortName(seat.name)}
              </text>
              {!seat.connected && (
                <circle className={styles.offline} cx={p.x + r * 0.72} cy={p.y - r * 0.72} r={5} />
              )}
              {!seat.alive && seat.ghost_vote_available && (
                <circle className={styles.ghost} cx={p.x - r * 0.72} cy={p.y + r * 0.72} r={5} />
              )}
              {showReady && seat.alive && seat.ready_for_night && (
                <circle className={styles.ready} cx={p.x + r * 0.72} cy={p.y + r * 0.72} r={5} />
              )}
              {vote && vote.yes !== null && (
                <g>
                  <circle
                    className={vote.yes && !vote.struck ? styles.voteYes : styles.voteNo}
                    cx={p.x - r * 0.72}
                    cy={p.y - r * 0.72}
                    r={9}
                  />
                  <text
                    className={styles.voteText}
                    x={p.x - r * 0.72}
                    y={p.y - r * 0.72}
                    fill={vote.yes && !vote.struck ? "#06140b" : "var(--fg-muted)"}
                  >
                    {vote.yes ? "✓" : "✗"}
                  </text>
                  {vote.struck && (
                    <line
                      className={styles.voteStrike}
                      x1={p.x - r * 0.72 - 6}
                      y1={p.y - r * 0.72 + 6}
                      x2={p.x - r * 0.72 + 6}
                      y2={p.y - r * 0.72 - 6}
                    />
                  )}
                </g>
              )}
            </g>
          );
        })}
      </svg>
      {children && <div className={styles.center}>{children}</div>}
      {seats.map(({ p, seat, tappable, isSelected }) => {
        const label = seatLabel(seat, view.me_seat_id, votes.get(seat.seat_id));
        const area = hitArea(p.x, p.y, layout.hitRadius);
        return tappable ? (
          <button
            key={seat.seat_id}
            type="button"
            className={styles.hit}
            style={area}
            aria-label={label}
            aria-pressed={pickMode ? isSelected : undefined}
            onClick={() => onSeatTap!(seat.seat_id)}
          />
        ) : (
          <span
            key={seat.seat_id}
            role="img"
            className={styles.seatLabel}
            style={area}
            aria-label={label}
          />
        );
      })}
    </div>
  );
}

/** A seat's circle as a box over the drawing, in percentages of the (square) town square. */
function hitArea(x: number, y: number, radius: number): CSSProperties {
  const pct = (v: number) => `${(v / SIZE) * 100}%`;
  return {
    left: pct(x - radius),
    top: pct(y - radius),
    width: pct(2 * radius),
    height: pct(2 * radius),
  };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 1);
  return letters.toUpperCase();
}

function seatLabel(seat: SeatPublic, me: string, vote: VoteView | undefined): string {
  const bits = [seat.seat_id === me ? `${seat.name} (you)` : seat.name];
  if (!seat.alive) bits.push(seat.ghost_vote_available ? "dead, ghost vote left" : "dead");
  if (!seat.connected) bits.push("offline");
  if (vote?.struck) bits.push("voted yes, did not count");
  else if (vote?.yes != null) bits.push(vote.yes ? "voted yes" : "voted no");
  return bits.join(", ");
}
